/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link, useLocation, Navigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Check,
  ArrowLeft,
  CreditCard,
  Sparkles,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
  {
    id: "essentiel_monthly",
    name: "Essentiel",
    cycle: "Mensuel",
    price: "5 DT",
    sub: "par mois",
    features: ["Jusqu'à 50 têtes", "Carnet sanitaire", "Mobile hors-ligne"],
  },
  {
    id: "essentiel_annual",
    name: "Essentiel",
    cycle: "Annuel (-20%)",
    price: "48 DT",
    sub: "par an au lieu de 60 DT",
    badge: "Économisez 12 DT",
    features: ["Jusqu'à 50 têtes", "Carnet sanitaire", "Mobile hors-ligne"],
  },
  {
    id: "pro_monthly",
    name: "Pro",
    cycle: "Mensuel",
    price: "15 DT",
    sub: "par mois",
    features: ["Animaux illimités", "Reproduction", "Stocks & rations", "Analyses"],
    highlight: true,
  },
  {
    id: "pro_annual",
    name: "Pro",
    cycle: "Annuel (-20%)",
    price: "144 DT",
    sub: "par an au lieu de 180 DT",
    badge: "Économisez 36 DT",
    features: ["Animaux illimités", "Reproduction", "Stocks & rations", "Analyses"],
    highlight: true,
  },
];

export default function Pay() {
  const { user, loading } = useAuth();
  const [submitting, setSubmitting] = useState(null);
  const [error, setError] = useState("");

  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAF7] text-[#5C6B5C]">
        Chargement…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;

  const checkout = async (packageId) => {
    setError("");
    setSubmitting(packageId);
    try {
      const { data } = await axios.post(
        `${API}/payments/checkout`,
        { package_id: packageId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
      setSubmitting(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-12 px-6">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/dashboard/client"
          className="inline-flex items-center gap-2 text-sm text-[#5C6B5C] hover:text-[#1C3A27] mb-6"
          data-testid="pay-back"
        >
          <ArrowLeft size={16} /> Retour à mon espace
        </Link>

        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
            <Sparkles size={14} /> Activation immédiate
          </div>
          <h1 className="mt-3 font-display text-3xl md:text-5xl font-black text-[#1C3A27]">
            Choisissez votre formule.
          </h1>
          <p className="mt-4 text-[#5C6B5C] text-lg">
            Paiement sécurisé par carte bancaire via Stripe. Activation
            automatique dès la confirmation.
          </p>
        </div>

        {error && (
          <div className="mt-6 max-w-2xl mx-auto rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-4">
            {error}
          </div>
        )}

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PACKAGES.map((p) => (
            <div
              key={p.id}
              data-testid={`package-${p.id}`}
              className={`relative rounded-3xl p-6 border flex flex-col ${
                p.highlight
                  ? "bg-[#1C3A27] text-white border-[#1C3A27] shadow-2xl shadow-[#1C3A27]/15"
                  : "bg-white border-[#E2E4DF]"
              }`}
            >
              {p.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#E05A3D] text-white text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                  {p.badge}
                </div>
              )}
              <div className={p.highlight ? "" : "text-[#1C3A27]"}>
                <div className="text-xs uppercase tracking-widest font-bold opacity-70">
                  {p.cycle}
                </div>
                <h3 className="mt-1 font-display text-2xl font-bold">
                  {p.name}
                </h3>
                <div className="mt-5 font-display text-4xl font-black">
                  {p.price}
                </div>
                <div
                  className={`text-sm mt-1 ${
                    p.highlight ? "text-white/60" : "text-[#5C6B5C]"
                  }`}
                >
                  {p.sub}
                </div>
              </div>
              <ul className="mt-6 space-y-2 flex-1 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check
                      size={14}
                      strokeWidth={3}
                      className={`mt-0.5 shrink-0 ${
                        p.highlight ? "text-[#E05A3D]" : "text-[#1C3A27]"
                      }`}
                    />
                    <span className={p.highlight ? "text-white/90" : ""}>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`buy-${p.id}`}
                onClick={() => checkout(p.id)}
                disabled={submitting !== null}
                className={`mt-6 h-11 rounded-xl font-semibold ${
                  p.highlight
                    ? "bg-[#E05A3D] hover:bg-[#C84B31] text-white"
                    : "bg-[#1C3A27] hover:bg-[#142A1C] text-white"
                }`}
              >
                {submitting === p.id ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    <CreditCard size={16} className="me-2" />
                    Payer maintenant
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-[#5C6B5C] mt-8 max-w-md mx-auto">
          Tarifs en Dinar Tunisien — converti en USD au moment du paiement test
          Stripe. En production, le paiement TND direct sera disponible via
          Konnect.
        </p>
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "pending", attempts: 0 });

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(
          `${API}/payments/status/${sessionId}`,
          { withCredentials: true }
        );
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", attempts });
          await refresh();
          return;
        }
        if (data.status === "expired" || attempts >= maxAttempts) {
          setState({ status: data.status === "expired" ? "expired" : "timeout", attempts });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setState({ status: "error", attempts });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-[#FAFAF7] grid place-items-center px-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[#E2E4DF] p-10 text-center">
        {state.status === "pending" && (
          <>
            <Loader2 size={42} className="mx-auto text-[#1C3A27] animate-spin" />
            <h1 className="mt-5 font-display text-2xl font-black text-[#1C3A27]">
              Vérification du paiement…
            </h1>
            <p className="mt-2 text-[#5C6B5C] text-sm">
              Merci de patienter quelques secondes.
            </p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto">
              <CheckCircle2 size={32} className="text-emerald-700" />
            </div>
            <h1 className="mt-5 font-display text-3xl font-black text-[#1C3A27]">
              Paiement confirmé !
            </h1>
            <p className="mt-3 text-[#5C6B5C]">
              Votre compte est désormais <strong>actif</strong>. Bienvenue chez
              Mazra3ti.
            </p>
            <Link to="/dashboard/client">
              <Button
                data-testid="pay-success-continue"
                className="mt-7 w-full h-12 rounded-xl bg-[#1C3A27] hover:bg-[#142A1C] text-white"
              >
                Accéder à mon espace
              </Button>
            </Link>
          </>
        )}
        {(state.status === "timeout" || state.status === "error") && (
          <>
            <Loader2 size={42} className="mx-auto text-[#E05A3D]" />
            <h1 className="mt-5 font-display text-2xl font-black text-[#1C3A27]">
              Paiement en cours
            </h1>
            <p className="mt-3 text-[#5C6B5C]">
              Le paiement met plus de temps que prévu à se confirmer. Vous
              recevrez un message dès l'activation. Vous pouvez aussi
              actualiser cette page.
            </p>
            <Link to="/dashboard/client">
              <Button variant="outline" className="mt-7 w-full">
                Retour à mon espace
              </Button>
            </Link>
          </>
        )}
        {state.status === "expired" && (
          <>
            <XCircle size={42} className="mx-auto text-red-600" />
            <h1 className="mt-5 font-display text-2xl font-black text-[#1C3A27]">
              Session expirée
            </h1>
            <p className="mt-3 text-[#5C6B5C]">
              La session de paiement a expiré. Vous pouvez réessayer.
            </p>
            <Link to="/pay">
              <Button className="mt-7 w-full h-12 rounded-xl bg-[#1C3A27] text-white">
                Réessayer
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
